import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Ban,
  Bell,
  CheckCircle2,
  Clock,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  FileDown,
  FileText,
  Mail,
  Paperclip,
  Pencil,
  Plus,
  Receipt,
  Send,
  ShieldCheck,
  ShieldX,
  Trash2,
  Upload,
  Wallet,
  X,
} from 'lucide-react';
import type { MenuItem } from '@/components/menu';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { IconButton } from '@/components/icon-button';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { SaveAddressDialog } from '@/components/save-address-dialog';
import { Select } from '@/components/select';
import { EmailComposerDialog, type ComposedEmail } from '@/components/email-composer';
import { Textarea } from '@/components/textarea';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  summarizeRecipients,
} from '@/lib/mail-recipients';
import { useFormError } from '@/lib/form-error';
import { centsToDollarsInput, dollars, dollarsToCents, formatDateLong } from '@/lib/format';
import { useClientBillable } from '@/lib/expenses';
import { useBusinessSettings } from '@/lib/business-settings';
import {
  invoiceDeleteWarning,
  openInvoicePdf,
  useChargeInvoiceOnFile,
  useClientInvoices,
  useClientTaxRate,
  useCreateInvoice,
  useDeleteInvoice,
  useDeleteInvoiceDocument,
  useIssueInvoice,
  useRecordInvoicePayment,
  useRemindInvoice,
  useRevokeInvoiceAuthorization,
  useSendInvoice,
  useSendInvoiceReceipt,
  useUpdateInvoice,
  useUploadInvoiceDocument,
  useVoidInvoice,
  type InvoiceInput,
  type InvoiceLineKind,
  type RecordPaymentInput,
} from '@/lib/invoices';
import { usePaymentMethods } from '@/lib/payment-methods';
import { PaymentsPanel } from './payments-panel';
import type { EmailDraftData, ExpenseData, InvoiceData, TimeEntryData } from '@/types/api';
import type { EmailType } from '@/lib/email-composer';

type PaymentMethod = RecordPaymentInput['method'];

const BUILDER_KINDS: InvoiceLineKind[] = ['service', 'item', 'discount'];
const KIND_LABEL: Record<string, string> = {
  service: 'Service',
  time: 'Time',
  expense: 'Expense',
  item: 'Item',
  discount: 'Discount',
};

function statusTone(status: string): 'success' | 'warning' | 'neutral' | 'info' {
  if (status === 'paid') return 'success';
  if (status === 'partial') return 'info';
  if (status === 'sent') return 'warning';
  return 'neutral';
}

export function ClientInvoices({ clientId }: { clientId: number }) {
  const { data, isLoading } = useClientInvoices(clientId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const list = data?.data ?? [];
  const editing = editingId != null ? list.find((i) => i.id === editingId) ?? null : null;

  // The Overview "Bill" quick action links here with ?new=1 to jump straight
  // into the builder. Consume the flag once, then strip it from the URL.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setCreateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const outstanding = list
    .filter((i) => i.status !== 'void')
    .reduce((sum, i) => sum + i.balance_cents, 0);

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="grid grid-cols-3 divide-x divide-[var(--color-border)] text-center">
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
              Outstanding
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">{dollars(outstanding)}</div>
          </div>
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
              Drafts
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">
              {list.filter((i) => i.status === 'draft').length}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
              Total invoices
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">{list.length}</div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" /> New invoice
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : list.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No invoices yet"
          description="Build a custom invoice with line items, then save it as a draft."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> New invoice
            </Button>
          }
        />
      ) : (
        <Card className="divide-y divide-[var(--color-border)] overflow-hidden">
          {list.map((invoice) => (
            <InvoiceRow
              key={invoice.id}
              invoice={invoice}
              clientId={clientId}
              onEdit={() => setEditingId(invoice.id)}
            />
          ))}
        </Card>
      )}

      <PaymentsPanel clientId={clientId} invoices={list} />

      <InvoiceBuilder open={createOpen} clientId={clientId} onClose={() => setCreateOpen(false)} />
      <InvoiceBuilder
        clientId={clientId}
        invoice={editing}
        onClose={() => setEditingId(null)}
      />
    </div>
  );
}

function InvoiceRow({
  invoice,
  clientId,
  onEdit,
}: {
  invoice: InvoiceData;
  clientId: number;
  onEdit: () => void;
}) {
  const del = useDeleteInvoice(clientId);
  const send = useSendInvoice(clientId);
  const issue = useIssueInvoice(clientId);
  const remind = useRemindInvoice(clientId);
  const voidInvoice = useVoidInvoice(clientId);
  const chargeOnFile = useChargeInvoiceOnFile(clientId);
  const sendReceipt = useSendInvoiceReceipt(clientId);
  const revokeAuthorization = useRevokeInvoiceAuthorization(clientId);
  const paymentMethods = usePaymentMethods(clientId);
  const confirm = useConfirm();
  const toast = useToast();
  const [payOpen, setPayOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  // Which email the send dialog is currently composing, and any addresses that
  // just went out without belonging to a contact.
  const [mailAction, setMailAction] = useState<MailAction | null>(null);
  const [unsavedAddresses, setUnsavedAddresses] = useState<string[]>([]);

  const cardOnFile =
    paymentMethods.data?.find((pm) => pm.is_default) ?? paymentMethods.data?.[0] ?? null;

  const isDraft = invoice.status === 'draft';
  const isVoid = invoice.status === 'void';
  const isPaid = invoice.status === 'paid';
  const isOpen = invoice.status === 'sent' || invoice.status === 'partial';
  const hasBalance = invoice.balance_cents > 0;
  const isExternal = invoice.is_external;

  async function run(fn: () => Promise<unknown>, message: string) {
    try {
      await fn();
      toast.success(message);
    } catch (err) {
      toast.error('Something went wrong', err instanceof Error ? err.message : undefined);
    }
  }

  const mail = mailAction ? MAIL_ACTIONS[mailAction] : null;
  const mailPending = send.isPending || remind.isPending || sendReceipt.isPending;

  async function sendMail(email: ComposedEmail, draft: EmailDraftData) {
    if (!mailAction) return;

    // A failure throws back into the composer, which stays open with the
    // wording and recipients intact and shows why.
    const mutation = { send, remind, receipt: sendReceipt }[mailAction];
    await mutation.mutateAsync({ id: invoice.id, ...email });

    toast.success(
      `${MAIL_ACTIONS[mailAction].toastLabel} ${invoice.number} to ${summarizeRecipients(email)}`,
    );
    setMailAction(null);

    // Anything typed into the dialog that no contact owns is worth keeping.
    const known = new Set((draft.recipients?.options ?? []).map((o) => o.address.toLowerCase()));
    const strays = [...email.to, ...email.cc].filter((a) => !known.has(a.toLowerCase()));
    if (strays.length > 0) setUnsavedAddresses(strays);
  }

  const items: MenuItem[] = [];

  if (isDraft) {
    // External invoices were sent from another system — mark them issued
    // (no email) so a payment can be recorded. In-app drafts get Send.
    items.push({
      key: 'issue',
      label: 'Mark issued',
      icon: CheckCircle2,
      onSelect: () => run(() => issue.mutateAsync(invoice.id), `${invoice.number} marked issued`),
    });
    if (!isExternal) {
      items.push({
        key: 'send',
        label: 'Send',
        icon: Send,
        onSelect: () => setMailAction('send'),
      });
    }
  } else if (isOpen && !isExternal) {
    items.push({
      key: 'resend',
      label: 'Resend',
      icon: Send,
      onSelect: () => setMailAction('send'),
    });
  }

  items.push({
    key: 'documents',
    label: 'Documents',
    icon: FileText,
    onSelect: () => setDocsOpen(true),
  });

  if (!isVoid) {
    items.push({
      key: 'preview',
      label: 'Preview',
      icon: ExternalLink,
      disabled: !invoice.public_url,
      onSelect: () => {
        if (invoice.public_url) window.open(invoice.public_url, '_blank', 'noopener,noreferrer');
      },
    });
    items.push({
      key: 'copy',
      label: 'Copy link',
      icon: Copy,
      disabled: !invoice.public_url,
      onSelect: async () => {
        if (!invoice.public_url) return;
        await navigator.clipboard.writeText(invoice.public_url);
        toast.success('Link copied');
      },
    });
  }

  if (invoice.pdf_url) {
    const pdfUrl = invoice.pdf_url;
    items.push({
      key: 'pdf',
      label: 'Download PDF',
      icon: FileDown,
      onSelect: async () => {
        try {
          await openInvoicePdf(pdfUrl);
        } catch {
          toast.error('Could not open the PDF');
        }
      },
    });
  }

  if (isOpen && hasBalance) {
    if (!isExternal) {
      items.push({
        key: 'remind',
        label: 'Send reminder',
        icon: Bell,
        onSelect: () => setMailAction('remind'),
      });
    }
    if (cardOnFile) {
      const brand = cardOnFile.brand
        ? cardOnFile.brand.charAt(0).toUpperCase() + cardOnFile.brand.slice(1)
        : null;
      const label = brand ? `${brand} ···· ${cardOnFile.last4 ?? ''}`.trim() : 'the card on file';
      items.push({
        key: 'charge',
        label: 'Charge card on file',
        icon: CreditCard,
        onSelect: async () => {
          const ok = await confirm({
            title: `Charge ${dollars(invoice.balance_cents)}?`,
            body: `This charges ${label} for invoice ${invoice.number}. The client gets a receipt automatically.`,
            confirmLabel: 'Charge card',
          });
          if (ok) {
            run(
              () => chargeOnFile.mutateAsync(invoice.id),
              `Charged ${dollars(invoice.balance_cents)} to ${label}`,
            );
          }
        },
      });
    }
    items.push({
      key: 'record',
      label: 'Record payment',
      icon: Wallet,
      onSelect: () => setPayOpen(true),
    });
  }

  if (isDraft) {
    items.push({ key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit });
  }

  if (invoice.amount_paid_cents > 0) {
    items.push({
      key: 'email-receipt',
      label: 'Email receipt',
      icon: Mail,
      onSelect: () => setMailAction('receipt'),
    });
  }

  if (isPaid && invoice.receipt_url) {
    const url = invoice.receipt_url;
    items.push({
      key: 'receipt',
      label: 'Download receipt',
      icon: Download,
      onSelect: () => window.open(url, '_blank', 'noopener,noreferrer'),
    });
  }

  if (invoice.authorization) {
    const auth = invoice.authorization;
    if (auth.pdf_url) {
      const url = auth.pdf_url;
      items.push({
        key: 'auth-download',
        label: 'Download authorization',
        icon: ShieldCheck,
        onSelect: () => window.open(url, '_blank', 'noopener,noreferrer'),
      });
    }
    items.push({
      key: 'auth-revoke',
      label: 'Revoke authorization',
      icon: ShieldX,
      danger: true,
      onSelect: async () => {
        const ok = await confirm({
          title: 'Revoke authorization?',
          body: `Removes the client's signed consent to charge the card on file for ${invoice.number}. The signed record is kept.`,
          confirmLabel: 'Revoke',
          tone: 'danger',
        });
        if (ok) {
          await run(
            () => revokeAuthorization.mutateAsync({ invoiceId: invoice.id, authorizationId: auth.id }),
            'Authorization revoked',
          );
        }
      },
    });
  }

  if (!isPaid && !isVoid) {
    items.push({
      key: 'void',
      label: 'Void',
      icon: Ban,
      danger: true,
      onSelect: async () => {
        const ok = await confirm({
          title: `Void ${invoice.number}?`,
          body: 'The invoice will be marked void and can no longer be paid. This cannot be undone.',
          confirmLabel: 'Void invoice',
          tone: 'danger',
        });
        if (ok) await run(() => voidInvoice.mutateAsync(invoice.id), `${invoice.number} voided`);
      },
    });
  }

  // Available at any status, paid included: voiding keeps the record on the
  // books, deleting is for invoices that shouldn't exist at all.
  items.push({
    key: 'delete',
    label: 'Delete',
    icon: Trash2,
    danger: true,
    onSelect: async () => {
      const ok = await confirm({
        title: `Delete ${invoice.number}?`,
        body: invoiceDeleteWarning(invoice),
        confirmLabel: 'Delete invoice',
        tone: 'danger',
      });
      if (ok) await run(() => del.mutateAsync(invoice.id), `${invoice.number} deleted`);
    },
  });

  const partiallyPaid = invoice.amount_paid_cents > 0 && !isPaid;

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <button
        type="button"
        onClick={isDraft ? onEdit : undefined}
        className={`min-w-0 flex-1 text-left ${isDraft ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium tabular-nums">{invoice.number}</span>
          <Badge tone={statusTone(invoice.status)}>{invoice.status_label}</Badge>
          {isExternal ? <Badge tone="neutral">External</Badge> : null}
          {invoice.is_overdue ? <Badge tone="danger">Overdue</Badge> : null}
          {invoice.authorization ? (
            <Badge tone="info" title={`SMS-verified${invoice.authorization.signer_name ? ` by ${invoice.authorization.signer_name}` : ''}`}>
              Authorized
            </Badge>
          ) : null}
        </div>
        <div className="mt-0.5 text-xs text-[var(--color-foreground-subtle)]">
          Issued {formatDateLong(invoice.issue_date)}
          {invoice.due_date ? ` · due ${formatDateLong(invoice.due_date)}` : ''}
          {invoice.external_reference ? ` · ref ${invoice.external_reference}` : ''}
          {invoice.documents.length > 0 ? ` · ${invoice.documents.length} file${invoice.documents.length === 1 ? '' : 's'}` : ''}
        </div>
        {invoice.paid_by ? (
          <div className="mt-0.5 text-xs text-[var(--color-success)]">
            Paid by {invoice.paid_by}
            {invoice.paid_at ? ` · ${formatDateLong(invoice.paid_at)}` : ''}
          </div>
        ) : null}
      </button>

      <div className="text-right">
        <div className="font-semibold tabular-nums">{dollars(invoice.total_cents)}</div>
        {partiallyPaid ? (
          <div className="text-xs text-[var(--color-foreground-subtle)] tabular-nums">
            {dollars(invoice.amount_paid_cents)} of {dollars(invoice.total_cents)} paid
          </div>
        ) : !isPaid && !isVoid && invoice.balance_cents !== invoice.total_cents ? (
          <div className="text-xs text-[var(--color-foreground-subtle)] tabular-nums">
            {dollars(invoice.balance_cents)} due
          </div>
        ) : null}
      </div>

      <Menu items={items} />

      <RecordPaymentDialog
        clientId={clientId}
        invoice={invoice}
        open={payOpen}
        onClose={() => setPayOpen(false)}
      />

      <DocumentsDialog
        clientId={clientId}
        invoice={invoice}
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
      />

      {mail ? (
        <EmailComposerDialog
          source={{ kind: 'record', type: mail.type, id: invoice.id }}
          onClose={() => setMailAction(null)}
          title={`${mail.title} ${invoice.number}`}
          summary={<p className="text-sm text-[var(--color-foreground-muted)]">{mail.subtitle}</p>}
          confirmLabel={mail.confirmLabel}
          pending={mailPending}
          onSend={sendMail}
        />
      ) : null}

      {unsavedAddresses.length > 0 ? (
        <SaveAddressDialog
          clientId={clientId}
          addresses={unsavedAddresses}
          onDone={() => setUnsavedAddresses([])}
        />
      ) : null}
    </div>
  );
}

type MailAction = 'send' | 'remind' | 'receipt';

const MAIL_ACTIONS: Record<
  MailAction,
  { type: EmailType; title: string; subtitle: string; confirmLabel: string; toastLabel: string }
> = {
  send: {
    type: 'invoice.sent',
    title: 'Send',
    subtitle: 'Emails a link to the public pay page, with the invoice PDF attached.',
    confirmLabel: 'Send invoice',
    toastLabel: 'Sent',
  },
  remind: {
    type: 'invoice.reminder',
    title: 'Send a reminder for',
    subtitle: 'Emails another copy of the pay link for the balance still owed.',
    confirmLabel: 'Send reminder',
    toastLabel: 'Reminder sent for',
  },
  receipt: {
    type: 'invoice.receipt',
    title: 'Email the receipt for',
    subtitle: 'Emails the receipt PDF for the payment already recorded.',
    confirmLabel: 'Send receipt',
    toastLabel: 'Receipt sent for',
  },
};


function DocumentsDialog({
  clientId,
  invoice,
  open,
  onClose,
}: {
  clientId: number;
  invoice: InvoiceData;
  open: boolean;
  onClose: () => void;
}) {
  const upload = useUploadInvoiceDocument(clientId);
  const remove = useDeleteInvoiceDocument(clientId);
  const confirm = useConfirm();
  const toast = useToast();

  async function onFile(e: FormEvent<HTMLInputElement> & { currentTarget: HTMLInputElement }) {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = '';
    if (!file) return;
    try {
      await upload.mutateAsync({ id: invoice.id, file });
      toast.success('Document uploaded');
    } catch (err) {
      toast.error('Upload failed', err instanceof Error ? err.message : undefined);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={`Documents · ${invoice.number}`} size="md">
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-foreground-muted)]">
          Attach the PDF of an invoice generated in another system, or any supporting file.
        </p>

        {invoice.documents.length > 0 ? (
          <ul className="divide-y divide-[var(--color-border)] rounded-md border border-[var(--color-border)]">
            {invoice.documents.map((doc) => (
              <li key={doc.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <Paperclip className="size-4 shrink-0 text-[var(--color-foreground-muted)]" />
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-w-0 flex-1 truncate hover:underline"
                >
                  {doc.file_name}
                </a>
                <IconButton
                  type="button"
                  aria-label={`Remove ${doc.file_name}`}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Remove ${doc.file_name}?`,
                      body: 'The file will be detached from this invoice.',
                      confirmLabel: 'Remove',
                      tone: 'danger',
                    });
                    if (ok) {
                      await remove.mutateAsync({ id: invoice.id, mediaId: doc.id });
                      toast.success('Document removed');
                    }
                  }}
                >
                  <X className="size-4" />
                </IconButton>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-md bg-[var(--color-surface-muted)] px-3 py-6 text-center text-sm text-[var(--color-foreground-subtle)]">
            No documents yet.
          </p>
        )}

        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-[var(--color-border)] px-3 py-3 text-sm font-medium text-[var(--color-foreground-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-foreground)]">
          <Upload className="size-4" />
          {upload.isPending ? 'Uploading…' : 'Upload document'}
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif"
            className="hidden"
            disabled={upload.isPending}
            onChange={onFile}
          />
        </label>

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'ach', label: 'ACH / bank transfer' },
  { value: 'other', label: 'Other' },
];

function RecordPaymentDialog({
  clientId,
  invoice,
  open,
  onClose,
}: {
  clientId: number;
  invoice: InvoiceData;
  open: boolean;
  onClose: () => void;
}) {
  const record = useRecordInvoicePayment(clientId);
  const toast = useToast();
  const { error, wrap, clear } = useFormError();
  const today = new Date().toISOString().slice(0, 10);

  const [seeded, setSeeded] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('check');
  const [paidAt, setPaidAt] = useState(today);
  const [reference, setReference] = useState('');

  // Prefill the amount with the outstanding balance each time the dialog opens.
  if (open && !seeded) {
    setAmount(centsToDollarsInput(invoice.balance_cents));
    setMethod('check');
    setPaidAt(today);
    setReference('');
    clear();
    setSeeded(true);
  }

  function close() {
    setSeeded(false);
    onClose();
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const cents = dollarsToCents(amount) ?? 0;
    if (cents <= 0) {
      toast.error('Enter a payment amount');
      return;
    }
    if (cents > invoice.balance_cents) {
      toast.error('Payment exceeds the balance due');
      return;
    }

    const input: RecordPaymentInput = {
      amount_cents: cents,
      method,
      paid_at: paidAt || null,
      reference: reference.trim() || null,
    };

    const ok = await wrap(() => record.mutateAsync({ id: invoice.id, input }));
    if (ok) {
      toast.success(`Payment recorded for ${invoice.number}`);
      close();
    }
  }

  return (
    <Dialog open={open} onClose={close} title={`Record payment · ${invoice.number}`} size="sm">
      <form onSubmit={submit} className="space-y-3">
        <div className="rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-sm">
          <span className="text-[var(--color-foreground-muted)]">Balance due </span>
          <span className="font-semibold tabular-nums">{dollars(invoice.balance_cents)}</span>
        </div>

        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Amount</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Method</span>
            <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Date</span>
            <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
          </label>
        </div>

        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Reference</span>
          <Input
            placeholder="Check number, transfer note, etc."
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </label>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" disabled={record.isPending}>
            {record.isPending ? 'Saving…' : 'Record payment'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

type LineRow = {
  kind: InvoiceLineKind;
  description: string;
  quantity: string;
  unitDollars: string;
  taxable: boolean;
  // Source references, set when the line is pulled from unbilled work.
  timeEntryId?: number | null;
  expenseId?: number | null;
};

function emptyLine(): LineRow {
  // WA taxes most goods and services (IT & web work included), so lines start
  // taxable; the admin can uncheck per line.
  return { kind: 'service', description: '', quantity: '1', unitDollars: '', taxable: true };
}

function hoursOf(entry: TimeEntryData): string {
  return (entry.duration_seconds / 3600).toFixed(2);
}

function timeEntryLine(entry: TimeEntryData): LineRow {
  return {
    kind: 'time',
    description: entry.description || 'Time',
    quantity: hoursOf(entry),
    unitDollars: centsToDollarsInput(entry.hourly_rate_cents ?? 0),
    taxable: true,
    timeEntryId: entry.id,
  };
}

function expenseLine(expense: ExpenseData): LineRow {
  return {
    kind: 'expense',
    description: expense.vendor ? `${expense.vendor}: ${expense.description}` : expense.description,
    quantity: '1',
    unitDollars: centsToDollarsInput(expense.amount_cents),
    taxable: true,
    expenseId: expense.id,
  };
}

function lineAmountCents(row: LineRow): number {
  const qty = parseFloat(row.quantity || '0') || 0;
  const unit = dollarsToCents(row.unitDollars) ?? 0;
  return Math.round(qty * unit);
}

function InvoiceBuilder({
  clientId,
  invoice,
  open,
  onClose,
}: {
  clientId: number;
  invoice?: InvoiceData | null;
  open?: boolean;
  onClose: () => void;
}) {
  const isEdit = !!invoice;
  const isOpen = isEdit ? invoice != null : !!open;
  const create = useCreateInvoice(clientId);
  const update = useUpdateInvoice(clientId);
  const uploadDoc = useUploadInvoiceDocument(clientId);
  const issue = useIssueInvoice(clientId);
  const toast = useToast();
  const { error, wrap } = useFormError();

  const today = new Date().toISOString().slice(0, 10);
  const [seeded, setSeeded] = useState<number | 'new' | null>(null);
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState('');
  const [taxPercent, setTaxPercent] = useState('0');
  const [jurisdiction, setJurisdiction] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const [externalRef, setExternalRef] = useState('');
  const [docFile, setDocFile] = useState<File | null>(null);

  const usedTimeIds = new Set(lines.map((l) => l.timeEntryId).filter((id): id is number => id != null));
  const usedExpenseIds = new Set(lines.map((l) => l.expenseId).filter((id): id is number => id != null));

  function addLines(next: LineRow[]) {
    setLines((prev) => {
      // Drop the leading blank placeholder so pulled work doesn't leave a gap.
      const kept = prev.filter(
        (l) => l.description.trim() !== '' || l.timeEntryId != null || l.expenseId != null,
      );
      const merged = [...kept, ...next];
      return merged.length > 0 ? merged : [emptyLine()];
    });
  }

  // Only look up the destination rate for brand-new invoices; edits keep the
  // rate saved on the record.
  const taxRateQuery = useClientTaxRate(clientId, isOpen && !isEdit);
  const businessSettings = useBusinessSettings();
  const defaultNotes = businessSettings.data?.default_invoice_notes ?? '';

  const seedKey: number | 'new' | null = invoice ? invoice.id : isOpen ? 'new' : null;
  if (seedKey !== null && seeded !== seedKey) {
    if (invoice) {
      setIssueDate(invoice.issue_date);
      setDueDate(invoice.due_date ?? '');
      setTaxPercent((invoice.tax_rate_bp / 100).toString());
      setJurisdiction(null);
      setNotes(invoice.notes ?? '');
      setIsExternal(invoice.is_external);
      setExternalRef(invoice.external_reference ?? '');
      setDocFile(null);
      setLines(
        invoice.line_items.length > 0
          ? invoice.line_items.map((li) => ({
              kind: li.kind as InvoiceLineKind,
              description: li.description,
              quantity: li.quantity.toString(),
              unitDollars: (li.unit_cents / 100).toString(),
              taxable: li.taxable,
              timeEntryId: li.time_entry_id,
              expenseId: li.expense_id,
            }))
          : [emptyLine()],
      );
    } else {
      setIssueDate(today);
      setDueDate('');
      setTaxPercent('0');
      setJurisdiction(null);
      setNotes(defaultNotes);
      setIsExternal(false);
      setExternalRef('');
      setDocFile(null);
      setLines([emptyLine()]);
    }
    setSeeded(seedKey);
  }

  // Pre-fill the tax rate from the client's destination address once the DOR
  // lookup resolves for a fresh invoice. Runs once per open (data + seeded are
  // stable afterward), so it never clobbers a rate the admin has hand-edited.
  useEffect(() => {
    if (isEdit || seeded !== 'new') return;
    const data = taxRateQuery.data;
    if (!data) return;
    setTaxPercent((data.rate_bp / 100).toString());
    setJurisdiction(data.jurisdiction);
  }, [taxRateQuery.data, isEdit, seeded]);

  // Prefill the notes with the business default once settings resolve for a
  // fresh invoice, but only while the box is still untouched so it never
  // clobbers something the admin typed (or an intentional clear once seeded).
  useEffect(() => {
    if (isEdit || seeded !== 'new' || !defaultNotes) return;
    setNotes((current) => (current === '' ? defaultNotes : current));
  }, [defaultNotes, isEdit, seeded]);

  const taxRateBp = Math.round((parseFloat(taxPercent || '0') || 0) * 100);
  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, l) => sum + lineAmountCents(l), 0);
    const taxableBase = lines
      .filter((l) => l.taxable)
      .reduce((sum, l) => sum + lineAmountCents(l), 0);
    const tax = Math.round((taxableBase * taxRateBp) / 10000);
    return { subtotal, tax, total: subtotal + tax };
  }, [lines, taxRateBp]);

  function updateLine(index: number, patch: Partial<LineRow>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  function handleClose() {
    setSeeded(null);
    onClose();
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: InvoiceInput = {
      issue_date: issueDate,
      due_date: dueDate || null,
      currency: 'USD',
      tax_rate_bp: taxRateBp,
      notes: notes || null,
      is_external: isExternal,
      external_reference: isExternal ? externalRef.trim() || null : null,
      line_items: lines
        .filter((l) => l.description.trim() !== '')
        .map((l) => ({
          kind: l.kind,
          description: l.description,
          quantity: parseFloat(l.quantity || '0') || 0,
          unit_cents: dollarsToCents(l.unitDollars) ?? 0,
          taxable: l.taxable,
          time_entry_id: l.timeEntryId ?? null,
          expense_id: l.expenseId ?? null,
        })),
    };

    const ok = await wrap(async () => {
      if (invoice) {
        await update.mutateAsync({ id: invoice.id, input: payload });
        return;
      }
      // New invoice. For an external one, log it, attach the PDF, and mark it
      // issued in one step so it's immediately ready to take a payment.
      const created = await create.mutateAsync(payload);
      if (isExternal) {
        if (docFile) {
          await uploadDoc.mutateAsync({ id: created.id, file: docFile });
        }
        await issue.mutateAsync(created.id);
      }
    });
    if (ok) {
      toast.success(
        invoice ? 'Invoice updated' : isExternal ? 'External invoice logged' : 'Draft invoice created',
      );
      setSeeded(null);
      onClose();
    }
  }

  const pending =
    create.isPending || update.isPending || uploadDoc.isPending || issue.isPending;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title={
        isEdit ? `Edit ${invoice?.number}` : isExternal ? 'Log external invoice' : 'New invoice'
      }
      size="xl"
    >
      <form onSubmit={submit} className="space-y-4">
        {!isEdit ? (
          <label className="flex items-start gap-2.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3">
            <input
              type="checkbox"
              checked={isExternal}
              onChange={(e) => setIsExternal(e.target.checked)}
              className="mt-0.5 size-4"
            />
            <span className="text-sm">
              <span className="font-medium text-[var(--color-foreground)]">
                Invoice generated elsewhere
              </span>
              <span className="mt-0.5 block text-xs text-[var(--color-foreground-muted)]">
                Log an invoice you created and sent from another system. Upload its PDF and record the
                total, this app won't email the client.
              </span>
            </span>
          </label>
        ) : null}

        {isExternal ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground-muted)]">
                External invoice #
              </span>
              <Input
                placeholder="e.g. QB-1042"
                value={externalRef}
                onChange={(e) => setExternalRef(e.target.value)}
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground-muted)]">Invoice PDF</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif"
                onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-[var(--color-foreground-muted)] file:mr-3 file:rounded-md file:border-0 file:bg-[var(--color-surface-muted)] file:px-3 file:py-1.5 file:text-sm file:font-medium"
              />
              <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
                Optional now, you can add or replace files later.
              </span>
            </label>
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Issue date</span>
            <Input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              required
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Due date</span>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Tax rate (%)</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={taxPercent}
              onChange={(e) => setTaxPercent(e.target.value)}
            />
            {!isEdit && jurisdiction ? (
              <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
                Destination rate for {jurisdiction}
              </span>
            ) : null}
          </label>
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-[96px_1fr_60px_84px_40px_84px_28px] items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
            <span>Kind</span>
            <span>Description</span>
            <span className="text-right">Qty</span>
            <span className="text-right">Unit $</span>
            <span className="text-center">Tax</span>
            <span className="text-right">Amount</span>
            <span />
          </div>

          {lines.map((line, index) => (
            <div
              key={index}
              className="grid grid-cols-[96px_1fr_60px_84px_40px_84px_28px] items-center gap-2"
            >
              <Select
                value={line.kind}
                onChange={(e) => {
                  const kind = e.target.value as InvoiceLineKind;
                  // Discounts are never taxable and are excluded from the tax base.
                  updateLine(index, { kind, taxable: kind === 'discount' ? false : line.taxable });
                }}
              >
                {(BUILDER_KINDS.includes(line.kind)
                  ? BUILDER_KINDS
                  : [line.kind, ...BUILDER_KINDS]
                ).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </Select>
              <Input
                placeholder="Description"
                value={line.description}
                onChange={(e) => updateLine(index, { description: e.target.value })}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                className="text-right"
                value={line.quantity}
                onChange={(e) => updateLine(index, { quantity: e.target.value })}
              />
              <Input
                type="number"
                step="0.01"
                className="text-right"
                value={line.unitDollars}
                onChange={(e) => updateLine(index, { unitDollars: e.target.value })}
              />
              <div className="flex justify-center">
                <input
                  type="checkbox"
                  checked={line.taxable}
                  disabled={line.kind === 'discount'}
                  onChange={(e) => updateLine(index, { taxable: e.target.checked })}
                  className="size-4 accent-[var(--color-primary)] disabled:opacity-40"
                  aria-label="Taxable"
                />
              </div>
              <div className="text-right text-sm tabular-nums">
                {dollars(lineAmountCents(line))}
              </div>
              <IconButton
                type="button"
                aria-label="Remove line"
                onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                disabled={lines.length === 1}
              >
                <Trash2 className="size-4" />
              </IconButton>
            </div>
          ))}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
            >
              <Plus className="size-4" /> Add line
            </Button>
            {!isExternal ? (
              <Button type="button" variant="secondary" onClick={() => setPickerOpen(true)}>
                <Clock className="size-4" /> Add from work
              </Button>
            ) : null}
          </div>
        </div>

        <div className="ml-auto w-56 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-[var(--color-foreground-muted)]">Subtotal</span>
            <span className="tabular-nums">{dollars(totals.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[var(--color-foreground-muted)]">
              Tax ({(taxRateBp / 100).toFixed(2)}%)
            </span>
            <span className="tabular-nums">{dollars(totals.tax)}</span>
          </div>
          <div className="flex justify-between border-t border-[var(--color-border)] pt-1 font-semibold">
            <span>Total</span>
            <span className="tabular-nums">{dollars(totals.total)}</span>
          </div>
        </div>

        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Notes</span>
          <Textarea
            rows={2}
            placeholder="Payment terms, thank-you note, or anything the client should see."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? 'Saving…'
              : isEdit
                ? 'Save'
                : isExternal
                  ? 'Log invoice'
                  : 'Save draft'}
          </Button>
        </div>
      </form>

      <AddFromWorkPicker
        clientId={clientId}
        invoiceId={invoice?.id}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        usedTimeIds={usedTimeIds}
        usedExpenseIds={usedExpenseIds}
        onPick={addLines}
      />
    </Dialog>
  );
}

function AddFromWorkPicker({
  clientId,
  invoiceId,
  open,
  onClose,
  usedTimeIds,
  usedExpenseIds,
  onPick,
}: {
  clientId: number;
  invoiceId: number | undefined;
  open: boolean;
  onClose: () => void;
  usedTimeIds: Set<number>;
  usedExpenseIds: Set<number>;
  onPick: (lines: LineRow[]) => void;
}) {
  const { data, isLoading } = useClientBillable(clientId, invoiceId, open);

  const timeEntries = (data?.time_entries ?? []).filter((e) => !usedTimeIds.has(e.id));
  const expenses = (data?.expenses ?? []).filter((e) => !usedExpenseIds.has(e.id));
  const isEmpty = timeEntries.length === 0 && expenses.length === 0;

  return (
    <Dialog open={open} onClose={onClose} title="Add from work" size="lg">
      <div className="space-y-5">
        {isLoading ? (
          <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
        ) : isEmpty ? (
          <EmptyState
            icon={Clock}
            title="Nothing to pull in"
            description="Unbilled time and billable expenses for this client will appear here."
          />
        ) : (
          <>
            {timeEntries.length > 0 ? (
              <PickerSection icon={Clock} title="Unbilled time">
                {timeEntries.map((entry) => (
                  <PickerRow
                    key={`t-${entry.id}`}
                    label={entry.description || 'Time'}
                    hint={`${hoursOf(entry)} h · ${formatDateLong(entry.started_at)}`}
                    amount={entry.amount_cents ?? 0}
                    onAdd={() => onPick([timeEntryLine(entry)])}
                  />
                ))}
              </PickerSection>
            ) : null}

            {expenses.length > 0 ? (
              <PickerSection icon={Wallet} title="Billable expenses">
                {expenses.map((expense) => (
                  <PickerRow
                    key={`e-${expense.id}`}
                    label={expense.vendor || expense.description}
                    hint={formatDateLong(expense.spent_on)}
                    amount={expense.amount_cents}
                    onAdd={() => onPick([expenseLine(expense)])}
                  />
                ))}
              </PickerSection>
            ) : null}

          </>
        )}

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function PickerSection({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Clock;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
        <Icon className="size-3.5" /> {title}
      </div>
      <Card className="divide-y divide-[var(--color-border)] overflow-hidden">{children}</Card>
    </div>
  );
}

function PickerRow({
  label,
  hint,
  amount,
  onAdd,
}: {
  label: string;
  hint: string;
  amount: number;
  onAdd: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{label}</div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">{hint}</div>
      </div>
      <div className="text-sm tabular-nums">{dollars(amount)}</div>
      <Button type="button" variant="secondary" onClick={onAdd}>
        <Plus className="size-4" /> Add
      </Button>
    </div>
  );
}
